import {
  Box,
  Button,
  IconButton,
  TableCell,
  Tooltip,
  tooltipClasses,
  Table,
  TableContainer,
  Divider,
  DialogContent,
  DialogActions
} from '@mui/material'
import {styled} from '@mui/material/styles'
import {Create, HistoryRounded, InfoOutlined} from '@mui/icons-material'

const getLabelWeight = (level, hasChildren, theme) => {
  if (level === 0 && hasChildren) {
    return theme.typography.fontWeightBold
  }
  if (hasChildren) {
    return theme.typography.fontWeightSemibold
  }
  return theme.typography.fontWeightRegular
}

export const StyledTableHeader = styled(TableCell)(({theme}) => ({
  fontWeight: theme.typography.fontWeightBold,
  border: `1px solid ${theme.palette.hpBaseLight}`,
  color: theme.palette.hpAccentBlack
}))

export const StyledTableHeaderDefault = styled(StyledTableHeader)(() => ({
  width: '45%'
}))

export const StyledTableHeaderCustom = styled(StyledTableHeader)(() => ({
  width: '45%'
}))

export const StyledTableHeaderActions = styled(StyledTableHeader)(() => ({
  width: '10%',
  textAlign: 'center'
}))

export const StyledDarkDot = styled(Box)(({theme}) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: theme.palette.hpPrimary,
  flexShrink: 0
}))

export const StyledLightDot = styled(Box)(({theme}) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: theme.palette.hpPrimaryLighter,
  flexShrink: 0
}))

export const StyledNoteSection = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  ':nth-of-type(1)': {
    borderRight: `1px solid ${theme.palette.hpBaseLight}`
  }
}))

export const StyledCustomTableRow = styled('tr')(({highlightRow, hasChildren, theme}) => ({
  backgroundColor: highlightRow ? theme.palette.hpBaseLighter : theme.palette.hpWhite,
  cursor: hasChildren ? 'pointer' : 'default',
  '& > td': {
    height: '40px',
    minHeight: '40px',
    maxHeight: '40px',
    padding: '0px 0px'
  }
}))

export const StyledCustomTableCell = styled('td')(({paddingLeft, theme}) => ({
  color: theme.palette.hpAccentBlack,
  paddingLeft,
  border: `1px solid ${theme.palette.hpBaseLight}`
}))

export const StyledCustomTableCellInput = styled('td', {
  shouldForwardProp: (prop) => prop !== 'hasError'
})(({theme, hasError}) => ({
  border: hasError ? `2px solid ${theme.palette.error.main} !important` : `1px solid ${theme.palette.hpBaseLight}`,
  padding: '0px 16px',
  position: 'relative',
  ...(hasError && {
    boxSizing: 'border-box'
  })
}))

export const StyledCustomTableCellActions = styled('td')(({theme}) => ({
  border: `1px solid ${theme.palette.hpBaseLight}`
}))

export const StyledToolTip = styled(({className, ...props}) => <Tooltip {...props} classes={{popper: className}} />)(
  ({theme}) => ({
    paddingRight: '8px',
    [`& .${tooltipClasses.tooltip}`]: {
      fontSize: '14px',
      backgroundColor: theme.palette.hpBaseDarker
    }
  })
)

export const StyledInfoIcon = styled(InfoOutlined)(({theme}) => ({
  color: theme.palette.hpBaseDark
}))

export const StyledLabelText = styled(Box)(({level, hasChildren, theme}) => ({
  fontWeight: getLabelWeight(level, hasChildren, theme)
}))

export const StyledEditIcon = styled(Create)(({theme}) => ({
  cursor: 'pointer',
  color: theme.palette.primary.main,
  fontSize: theme.typography.bodyLarge.fontSize
}))

export const StyledHistoryRoundedIcon = styled(HistoryRounded)(({theme}) => ({
  cursor: 'pointer',
  color: theme.palette.primary.main,
  fontSize: theme.typography.bodyLarge.fontSize
}))

export const StyledLabelTextContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  width: '100%',
  paddingRight: '8px'
}))

export const StyledTitleExpandedButton = styled(IconButton)(() => ({
  opacity: 0.4
}))

export const StyledRevertAllButton = styled(Button)(({theme}) => ({
  backgroundColor: theme.palette.hpWhite,
  color: theme.palette.primary.main,
  textDecoration: 'none',
  border: 'none',
  padding: '6px 0px',
  '&:hover': {
    backgroundColor: theme.palette.hpWhite,
    textDecoration: 'underline',
    border: 'none'
  },
  '&:disabled': {
    opacity: 0.4,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.hpWhite
  }
}))

export const StyledRevertAllIconButton = styled(IconButton)(() => ({
  padding: 0
}))

export const StyledRevertAllHistoryIcon = styled(HistoryRounded)(({theme}) => ({
  color: theme.palette.primary.main,
  fontSize: theme.typography.bodyLarge.fontSize
}))

export const StyledTableContainer = styled(TableContainer)(() => ({
  boxShadow: 'none'
}))

export const StyledTable = styled(Table)(() => ({
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  width: '100%'
}))

export const StyledExpandCollapseButton = styled(Button)(() => ({
  textDecoration: 'none',
  lineHeight: 'inherit',
  padding: '2px 0px',
  marginRight: '-8px'
}))

export const StyledDivider = styled(Divider)(({theme}) => ({
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5)
}))

export const StyledDialogContent = styled(DialogContent)(() => ({
  paddingTop: 0
}))

export const StyledDialogActions = styled(DialogActions)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}))

export const StyledNoteBox = styled(Box)(({theme}) => ({
  display: 'flex',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  justifyContent: 'space-between',
  alignItems: 'end'
}))

export const StyledNoteContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center'
}))

export const StyledErrorBox = styled(Box)(({theme}) => ({
  marginLeft: theme.spacing(1.5)
}))

export const StyledHeaderContentBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}))

export const StyledTooltipContainer = styled(Box)(({theme}) => ({
  fontSize: theme.typography.body1.fontSize
}))

export const StyledListContainer = styled('ul')(() => ({
  margin: 0,
  padding: 0,
  paddingLeft: '16px'
}))

export const StyledTooltipTitle = styled('div')(({theme}) => ({
  fontWeight: theme.typography.fontWeightBold
}))

export const StyledListItem = styled('li')(() => ({
  marginLeft: '8px',
  listStyleType: 'disc'
}))
